import React, { useState, useEffect } from 'react';
import { generateCoupon, listCoupons, resendCoupon, deleteCoupon } from '../../services/api/coupons';
import { format, parseISO } from 'date-fns';
import { Search, Send, AlertCircle, Filter, Trash2 } from 'lucide-react';
import type { Coupon } from '../../types/api';

interface CouponFormData {
  credits: number;
  recipientEmail: string;
  recipientName: string;
  isProspectiveClient: boolean;
}

interface GenerateCouponData {
  credits: number;
  recipientEmail: string;
  recipientName: string;
  type: 'existing' | 'prospective';
}

interface Filters {
  issuedDate: string;
  expiryDate: string;
  status: string;
  recipientName: string;
}

const CouponManagement: React.FC = () => {
  const [formData, setFormData] = useState<CouponFormData>({
    credits: 10,
    recipientEmail: '',
    recipientName: '',
    isProspectiveClient: false
  });
  const [filters, setFilters] = useState<Filters>({
    issuedDate: '',
    expiryDate: '',
    status: 'all',
    recipientName: ''
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [isResending, setIsResending] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [coupons, setCoupons] = useState<Coupon[]>([]);

  useEffect(() => {
    fetchCoupons();
    const interval = setInterval(fetchCoupons, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await listCoupons();
      if (response.success && Array.isArray(response.coupons)) {
        setCoupons(response.coupons);
      }
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    }
  };

  const handleGenerateCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsGenerating(true);

    try {
      const generateData: GenerateCouponData = {
        credits: formData.credits,
        recipientEmail: formData.recipientEmail,
        recipientName: formData.recipientName,
        type: formData.isProspectiveClient ? 'prospective' : 'existing'
      };

      const response = await generateCoupon(generateData);
      
      if (response.success) {
        setSuccess('Coupon generated and sent successfully!');
        setFormData({
          credits: 10,
          recipientEmail: '',
          recipientName: '',
          isProspectiveClient: false
        });
        await fetchCoupons();
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to generate coupon');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleResendCoupon = async (couponId: string) => {
    try {
      setIsResending(couponId);
      setError(null);
      setSuccess(null);
      await resendCoupon(couponId);
      setSuccess('Coupon reminder sent successfully!');
      await fetchCoupons();
    } catch (error) {
      setError('Failed to resend coupon');
    } finally {
      setIsResending(null);
      setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 3000);
    }
  };

  const handleDeleteCoupon = async (couponId: string) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) {
      return;
    }

    try {
      setIsDeleting(couponId);
      setError(null);
      await deleteCoupon(couponId);
      setSuccess('Coupon deleted successfully!');
      await fetchCoupons();
    } catch (error) {
      setError('Failed to delete coupon');
    } finally {
      setIsDeleting(null);
      setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 3000);
    }
  };

  const getCouponStatus = (coupon: Coupon) => {
    if (coupon.redemptions && coupon.redemptions.length > 0) {
      const redemption = coupon.redemptions[0];
      return {
        label: 'Redeemed',
        className: 'bg-green-100 text-green-800',
        description: `Redeemed by ${redemption.userName} on ${format(new Date(redemption.redeemedAt), 'PPP')}`,
        canResend: false,
        canDelete: false
      };
    }
    
    if (coupon.expiresAt && new Date(coupon.expiresAt) < new Date()) {
      return {
        label: 'Expired',
        className: 'bg-red-100 text-red-800',
        description: `Expired on ${format(new Date(coupon.expiresAt), 'PPP')}`,
        canResend: false,
        canDelete: true
      };
    }

    if (coupon.maxUses && coupon.currentUses >= coupon.maxUses) {
      return {
        label: 'Fully Redeemed',
        className: 'bg-yellow-100 text-yellow-800',
        description: `Maximum redemptions reached (${coupon.currentUses}/${coupon.maxUses})`,
        canResend: false,
        canDelete: false
      };
    }
    
    return {
      label: 'Available',
      className: 'bg-blue-100 text-blue-800',
      description: coupon.expiresAt 
        ? `Valid until ${format(new Date(coupon.expiresAt), 'PPP')}`
        : 'No expiration date',
      canResend: true,
      canDelete: true
    };
  };

  const filteredCoupons = coupons.filter(coupon => {
    const issuedDate = filters.issuedDate ? new Date(filters.issuedDate) : null;
    const expiryDate = filters.expiryDate ? new Date(filters.expiryDate) : null;
    const status = getCouponStatus(coupon).label.toLowerCase();
    const recipientName = coupon.metadata?.recipientName?.toLowerCase() || '';
    const recipientEmail = coupon.metadata?.recipientEmail?.toLowerCase() || '';

    return (
      (!issuedDate || format(new Date(coupon.createdAt), 'yyyy-MM-dd') === format(issuedDate, 'yyyy-MM-dd')) &&
      (!expiryDate || (coupon.expiresAt && format(new Date(coupon.expiresAt), 'yyyy-MM-dd') === format(expiryDate, 'yyyy-MM-dd'))) &&
      (filters.status === 'all' || status === filters.status.toLowerCase()) &&
      (filters.recipientName === '' || 
       recipientName.includes(filters.recipientName.toLowerCase()) ||
       recipientEmail.includes(filters.recipientName.toLowerCase()))
    );
  });

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Generate New Coupon</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 text-green-600 rounded-lg text-sm">
            {success}
          </div>
        )}

        <form onSubmit={handleGenerateCoupon} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Recipient Type
            </label>
            <div className="mt-2">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio text-cyan-600"
                  checked={!formData.isProspectiveClient}
                  onChange={() => setFormData({ ...formData, isProspectiveClient: false })}
                />
                <span className="ml-2">Existing User</span>
              </label>
              <label className="inline-flex items-center ml-6">
                <input
                  type="radio"
                  className="form-radio text-cyan-600"
                  checked={formData.isProspectiveClient}
                  onChange={() => setFormData({ ...formData, isProspectiveClient: true })}
                />
                <span className="ml-2">Prospective Client</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Recipient Email
            </label>
            <input
              type="email"
              value={formData.recipientEmail}
              onChange={(e) => setFormData({ ...formData, recipientEmail: e.target.value })}
              className="mt-1 block w-full rounded-md"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Recipient Name
            </label>
            <input
              type="text"
              value={formData.recipientName}
              onChange={(e) => setFormData({ ...formData, recipientName: e.target.value })}
              className="mt-1 block w-full rounded-md"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Credits Amount
            </label>
            <input
              type="number"
              value={formData.credits}
              onChange={(e) => setFormData({ ...formData, credits: parseFloat(e.target.value) })}
              className="mt-1 block w-full rounded-md"
              min="1"
              step="1"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isGenerating}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
          >
            {isGenerating ? 'Generating...' : 'Generate & Send Coupon'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <h2 className="text-xl font-semibold text-gray-900">Coupon Management</h2>
            <div className="flex flex-wrap gap-4">
              <input
                type="date"
                value={filters.issuedDate}
                onChange={(e) => setFilters({ ...filters, issuedDate: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Issue Date"
              />
              <input
                type="date"
                value={filters.expiryDate}
                onChange={(e) => setFilters({ ...filters, expiryDate: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Expiry Date"
              />
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="all">All Status</option>
                <option value="available">Available</option>
                <option value="redeemed">Redeemed</option>
                <option value="expired">Expired</option>
                <option value="fully redeemed">Fully Redeemed</option>
              </select>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by recipient..."
                  value={filters.recipientName}
                  onChange={(e) => setFilters({ ...filters, recipientName: e.target.value })}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto" style={{ maxHeight: '600px' }}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recipient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issue Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expires
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCoupons.map((coupon) => {
                const status = getCouponStatus(coupon);
                return (
                  <tr key={coupon.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {coupon.code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {coupon.credits}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{coupon.metadata?.recipientName}</div>
                      <div className="text-sm text-gray-500">{coupon.metadata?.recipientEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {coupon.metadata?.type === 'prospective' ? 'Prospective Client' : 'Existing User'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${status.className}`}>
                        {status.label}
                      </span>
                      <div className="text-xs text-gray-500 mt-1">
                        {status.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(coupon.createdAt), 'PPP')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {coupon.expiresAt ? format(new Date(coupon.expiresAt), 'PPP') : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        {status.canResend && (
                          <button
                            onClick={() => handleResendCoupon(coupon.id)}
                            disabled={isResending === coupon.id}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                          >
                            <Send className="h-4 w-4 mr-1" />
                            {isResending === coupon.id ? 'Sending...' : 'Resend'}
                          </button>
                        )}
                        {status.canDelete && (
                          <button
                            onClick={() => handleDeleteCoupon(coupon.id)}
                            disabled={isDeleting === coupon.id}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            {isDeleting === coupon.id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CouponManagement;